import { supabase } from '../lib/supabase';

/** CBM per pallet for pallet-count calculation. Pallets = ceil(totalCbm / CBM_PER_PALLET). */
export const CBM_PER_PALLET = 1.6;

export interface LmdRate {
  id: number;
  country_code: string;
  rate_type: 'ex_diesel' | 'incl_diesel';
  pallets: number;
  rate_eur: number;
  is_active: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface LmdCalculation {
  baseRate: number;
  dieselSurcharge: number;
  /** Fixed surcharge for NL and BE pallet shipments: setting value × pallet count. */
  palletSurcharge: number;
  palletSurchargePerPallet: number;
  totalRate: number;
  palletCount: number;
  description: string;
  isCartonPricing: boolean;
  cartonCount: number;
}

export interface LmdSetting {
  id: number;
  setting_key: string;
  setting_value: number;
  setting_type: 'percentage' | 'fixed' | 'boolean';
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** Thrown when no matching row exists in lmd_rates — UI translates via i18n keys. */
export class LmdRateNotFoundError extends Error {
  readonly country: string;
  readonly pallets: number;
  readonly cartons: number;

  constructor(country: string, pallets: number, cartons: number) {
    super(
      `No LMD rate found for ${country} with ${pallets} pallets or ${cartons} cartons`,
    );
    this.name = "LmdRateNotFoundError";
    this.country = country;
    this.pallets = pallets;
    this.cartons = cartons;
  }
}

/**
 * Fetch all LMD rates for a specific country and rate type
 */
export async function getLmdRates(
  countryCode: string, 
  rateType: 'ex_diesel' | 'incl_diesel' = 'ex_diesel'
): Promise<LmdRate[]> {
  const { data, error } = await supabase
    .from('lmd_rates')
    .select('*')
    .eq('country_code', countryCode.toUpperCase())
    .eq('rate_type', rateType)
    .eq('is_active', true)
    .order('pallets');

  if (error) {
    console.error('Error fetching LMD rates:', error);
    throw error;
  }

  return data || [];
}

/**
 * Get specific LMD rate for exact pallet count.
 * Coerces pallet count to a non-negative integer (DB column is integer).
 */
export async function getLmdRate(
  countryCode: string,
  palletCount: number,
  rateType: 'ex_diesel' | 'incl_diesel' = 'ex_diesel'
): Promise<LmdRate | null> {
  const pallets = Math.max(0, Math.floor(Number(palletCount) || 0));
  const { data, error } = await supabase
    .from('lmd_rates')
    .select('*')
    .eq('country_code', countryCode.toUpperCase().trim())
    .eq('rate_type', rateType)
    .eq('pallets', pallets)
    .eq('is_active', true)
    .maybeSingle();

  if (error) {
    console.error('Error fetching LMD rate:', error);
    throw error;
  }

  return data;
}

/**
 * Calculate LMD cost for given parameters
 */
export async function calculateLmdCost(
  countryCode: string,
  palletCount: number,
  cartonCount: number = 0,
  includeDieselSurcharge: boolean = false,
  dieselSurchargePercent: number = 15,
  forceCartonRate: boolean = false
): Promise<LmdCalculation> {
  try {
    let rate: LmdRate | null = null;
    let baseRate = 0;
    let description = '';
    let actualPalletCount = palletCount;

    const normalizedPallets = Math.max(0, Math.floor(Number(palletCount) || 0));
    const normalizedCartons = Math.max(0, Math.floor(Number(cartonCount) || 0));
    actualPalletCount = normalizedPallets;

    // Prefer explicit forceCartonRate; only fall back to carton when no pallets were requested
    const useCartonRate =
      forceCartonRate || (normalizedPallets === 0 && normalizedCartons > 0);

    if (useCartonRate) {
      // Get carton rate (stored as pallets = 0)
      rate = await getLmdRate(countryCode, 0, 'ex_diesel');
      if (rate) {
        baseRate = rate.rate_eur * normalizedCartons;
        description = `LMD ${countryCode} (${normalizedCartons} carton${normalizedCartons !== 1 ? 's' : ''})`;
        actualPalletCount = 1; // For billing purposes, treat as 1 unit
      }
    }

    // Use pallet rate when shipment is in pallet mode
    if (!rate && normalizedPallets > 0) {
      // Determine effective pallet count (max 15 for rate lookup)
      const ratePalletCount = Math.min(normalizedPallets, 15);

      // Get the base rate (always ex_diesel from table)
      rate = await getLmdRate(countryCode, ratePalletCount, 'ex_diesel');

      if (rate) {
        baseRate = rate.rate_eur;
        description = `LMD ${countryCode} (${normalizedPallets} pallet${normalizedPallets !== 1 ? 's' : ''})`;
        if (normalizedCartons > 0) {
          description += ` (${normalizedCartons} cartons)`;
        }
        if (normalizedPallets > 15) {
          description += ` - using 15+ rate`;
        }
      }
    }

    if (!rate) {
      throw new LmdRateNotFoundError(
        countryCode.toUpperCase().trim(),
        normalizedPallets,
        normalizedCartons,
      );
    }

    // Never apply diesel surcharge for carton shipments (when forceCartonRate is true)
    // or for single carton shipments (normalizedCartons === 1)
    const shouldApplyDieselSurcharge =
      includeDieselSurcharge && !forceCartonRate && !useCartonRate && normalizedCartons !== 1;
    const dieselSurcharge = shouldApplyDieselSurcharge ? (baseRate * dieselSurchargePercent / 100) : 0;

    let palletSurcharge = 0;
    let palletSurchargePerPallet = 0;
    const tollCountry = countryCode.toUpperCase().trim();
    const isTollPalletShipment =
      (tollCountry === 'NL' || tollCountry === 'BE') &&
      !useCartonRate &&
      normalizedPallets > 0;
    if (isTollPalletShipment) {
      const nlSurchargeSetting = await getLmdSetting('nl_pallet_surcharge_eur');
      if (nlSurchargeSetting?.is_active !== false) {
        palletSurchargePerPallet = nlSurchargeSetting?.setting_value ?? 1.8;
        if (palletSurchargePerPallet > 0) {
          palletSurcharge = palletSurchargePerPallet * normalizedPallets;
          description += ` + €${palletSurchargePerPallet.toFixed(2)}/pallet tol (${normalizedPallets})`;
        }
      }
    }

    const totalRate = baseRate + dieselSurcharge + palletSurcharge;

    // Add diesel info to description only if surcharge is applied
    if (shouldApplyDieselSurcharge) {
      description += ` + ${dieselSurchargePercent}% diesel`;
    }

    return {
      baseRate,
      dieselSurcharge,
      palletSurcharge,
      palletSurchargePerPallet,
      totalRate,
      palletCount: actualPalletCount,
      description,
      isCartonPricing: useCartonRate,
      cartonCount: normalizedCartons,
    };
  } catch (error) {
    console.error('Error calculating LMD cost:', error);
    throw error;
  }
}

/**
 * Get all available countries with LMD rates
 */
export async function getLmdCountries(): Promise<string[]> {
  const { data, error } = await supabase
    .from('lmd_rates')
    .select('country_code')
    .eq('is_active', true)
    .order('country_code');

  if (error) {
    console.error('Error fetching LMD countries:', error);
    throw error;
  }

  // Return unique country codes
  const countries = [...new Set(data?.map(item => item.country_code) || [])];
  return countries;
}

/**
 * Admin function to update LMD rate
 */
export async function updateLmdRate(
  id: number,
  updates: Partial<Pick<LmdRate, 'rate_eur' | 'is_active' | 'notes'>>
): Promise<LmdRate> {
  const { data, error } = await supabase
    .from('lmd_rates')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating LMD rate:', error);
    throw error;
  }

  return data;
}

/**
 * Admin function to create new LMD rate
 */
export async function createLmdRate(
  rate: Omit<LmdRate, 'id' | 'created_at' | 'updated_at'>
): Promise<LmdRate> {
  const { data, error } = await supabase
    .from('lmd_rates')
    .insert([rate])
    .select()
    .single();

  if (error) {
    console.error('Error creating LMD rate:', error);
    throw error;
  }

  return data;
}

/**
 * Get LMD setting by key
 */
export async function getLmdSetting(settingKey: string): Promise<LmdSetting | null> {
  const { data, error } = await supabase
    .from('lmd_settings')
    .select('*')
    .eq('setting_key', settingKey)
    .eq('is_active', true)
    .maybeSingle();

  if (error) {
    console.error('Error fetching LMD setting:', error);
    throw error;
  }

  return data;
}

/**
 * Get all LMD settings
 */
export async function getAllLmdSettings(): Promise<LmdSetting[]> {
  const { data, error } = await supabase
    .from('lmd_settings')
    .select('*')
    .eq('is_active', true)
    .order('setting_key');

  if (error) {
    console.error('Error fetching LMD settings:', error);
    throw error;
  }

  return data || [];
}

/**
 * Update LMD setting
 */
export async function updateLmdSetting(
  id: number,
  updates: Partial<Pick<LmdSetting, 'setting_value' | 'description' | 'is_active'>>
): Promise<LmdSetting> {
  const { data, error } = await supabase
    .from('lmd_settings')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating LMD setting:', error);
    throw error;
  }

  return data;
}

/**
 * Get current diesel surcharge percentage from settings
 */
export async function getDieselSurchargePercentage(): Promise<number> {
  const setting = await getLmdSetting('diesel_surcharge_percentage');
  return setting?.setting_value || 15; // Default fallback
}

/**
 * Get cartons per pallet setting
 */
export async function getCartonsPerPallet(): Promise<number> {
  const setting = await getLmdSetting('cartons_per_pallet');
  return setting?.setting_value || 8; // Default fallback
}

/**
 * Get pallet threshold (cartons >= this → pallet mode). From lmd_settings.pallet_threshold.
 */
export async function getPalletThreshold(): Promise<number> {
  const setting = await getLmdSetting('pallet_threshold');
  return setting?.setting_value ?? 8;
}

/**
 * Get tol surcharge (EUR per pallet) from settings. Applied to NL and BE pallet shipments.
 */
export async function getNlPalletSurchargeEur(): Promise<number> {
  const setting = await getLmdSetting('nl_pallet_surcharge_eur');
  if (setting?.is_active === false) return 0;
  return setting?.setting_value ?? 1.8;
} 